import React from "react";
import { UploadCloud, FileText, X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { cardCls, labelCls } from "./tokens";

/**
 * UploadCard — file dropzone + uploaded file list.
 * Variants:
 *   - dropzone: empty (no files)
 *   - uploading: per-file spinner
 *   - success: list of uploaded files with size + type badge + remove
 *   - error: per-file red banner
 */
export default function UploadCard({
  title = "Upload Documents",
  hint = "Drag & drop or click to browse · PDF, JPG, PNG up to 10 MB",
  accept = "image/*,.pdf",
  multiple = true,
  files = [], // [{ name, size, type, status: "uploading"|"success"|"error", message }]
  onFilesPicked,
  onRemove,
  testId = "upload-card",
}) {
  const inputRef = React.useRef(null);

  const pick = () => inputRef.current?.click();
  const handleChange = (e) => {
    const f = Array.from(e.target.files || []);
    if (f.length) onFilesPicked?.(f);
    e.target.value = "";
  };

  return (
    <div className={`${cardCls} p-6 flex flex-col gap-4`} data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Uploads</span>
          <h3 className="font-bold text-base text-onedesk-textPrimary">
            {title}
          </h3>
        </div>
        <span className="text-xs font-medium text-onedesk-textSecondary">
          {files.length} file{files.length === 1 ? "" : "s"}
        </span>
      </div>

      <button
        type="button"
        onClick={pick}
        data-testid={`${testId}-dropzone`}
        className="group flex flex-col items-center justify-center gap-2 rounded-od-input border-2 border-dashed border-onedesk-border bg-onedesk-bg p-8 transition-colors hover:border-onedesk-primary hover:bg-onedesk-primary/[0.03]"
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-od-input bg-onedesk-card border border-onedesk-border group-hover:border-onedesk-primary transition-colors">
          <UploadCloud
            className="h-5 w-5 text-onedesk-primary"
            strokeWidth={2.25}
          />
        </span>
        <span className="font-bold text-sm text-onedesk-textPrimary">
          Click to upload or drag & drop
        </span>
        <span className="font-medium text-xs text-onedesk-textSecondary">
          {hint}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        data-testid={`${testId}-input`}
      />

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              data-testid={`${testId}-file-${i}`}
              className={`flex items-center gap-3 rounded-od-input border px-3 py-2 ${
                f.status === "error"
                  ? "border-onedesk-danger/30 bg-onedesk-danger/5"
                  : "border-onedesk-border bg-onedesk-card"
              }`}
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-od-input bg-onedesk-bg border border-onedesk-border shrink-0">
                <FileText className="h-4 w-4 text-onedesk-textSecondary" strokeWidth={2.25} />
              </span>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-bold text-sm text-onedesk-textPrimary truncate">
                  {f.name}
                </span>
                <span className="font-medium text-xs text-onedesk-textSecondary">
                  {f.type ? `${f.type} · ` : ""}
                  {f.size || ""}
                  {f.message ? ` · ${f.message}` : ""}
                </span>
              </div>
              {f.status === "uploading" && (
                <Loader2 className="h-4 w-4 text-onedesk-primary animate-spin" strokeWidth={2.25} />
              )}
              {f.status === "success" && (
                <CheckCircle2 className="h-4 w-4 text-onedesk-success" strokeWidth={2.25} />
              )}
              {f.status === "error" && (
                <AlertCircle className="h-4 w-4 text-onedesk-danger" strokeWidth={2.25} />
              )}
              {onRemove && f.status !== "uploading" && (
                <button
                  type="button"
                  onClick={() => onRemove(i, f)}
                  className="rounded-full p-1 hover:bg-onedesk-bg"
                  aria-label={`Remove ${f.name}`}
                  data-testid={`${testId}-remove-${i}`}
                >
                  <X className="h-4 w-4 text-onedesk-textSecondary" strokeWidth={2.25} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
